import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { Search, UserPlus, Check, X, MessageCircle, MessagesSquare, Plus, QrCode, ScanLine, Copy, Share2, Users, Phone } from "lucide-react";
import { api, formatError, fileUrl, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Screen, PageHeader, Empty } from "../components/ui-kit";
import RoleBadge from "../components/RoleBadge";
import QRScanModal from "../components/QRScanModal";

const Avatar = ({ path, picture, name, size = "w-11 h-11" }) => (
  <img src={fileUrl(path) || picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1a1424&color=D4AF37`} alt="" className={`${size} rounded-full object-cover border border-white/10`} />
);

export default function Friends() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("messages");
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [convos, setConvos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const [sheet, setSheet] = useState(false);      // FAB action sheet
  const [scanOpen, setScanOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [ref, setRef] = useState(null);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupPicks, setGroupPicks] = useState([]);

  const load = useCallback(() => {
    api.get("/friends").then((r) => setData(r.data)).catch((e) => console.error("friends load failed", e));
    api.get("/conversations").then((r) => setConvos(r.data)).catch((e) => console.error("conversations load failed", e));
    api.get("/groups").then((r) => setGroups(r.data)).catch((e) => console.error("groups load failed", e));
  }, []);
  useEffect(() => { load(); api.get("/referral").then((r) => setRef(r.data)).catch(() => {}); }, [load]);

  // Deep link: /friends?add=CODE opens the add-by-code flow
  useEffect(() => {
    const code = params.get("add");
    if (code) { setAddCode(code.toUpperCase()); setAddOpen(true); params.delete("add"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    try { const r = await api.get(`/users/search?q=${encodeURIComponent(val)}`); setResults(r.data); } catch (e) { console.error("User search failed", e); setResults([]); }
  };

  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const request = (id) => act(async () => { await api.post("/friends/request", { user_id: id }); toast.success("Request sent"); setResults([]); setQ(""); });
  const accept = (rid) => act(async () => { await api.post("/friends/accept", { request_id: rid }); toast.success("Friend added"); });
  const remove = (id) => act(async () => { await api.post("/friends/remove", { user_id: id }); toast.success("Removed"); });

  const submitCode = () => act(async () => {
    const r = await api.post("/friends/request-by-code", { code: addCode });
    toast.success(`Request sent to ${r.data.name}`);
    setAddCode(""); setAddOpen(false);
  });

  const togglePick = (id) => setGroupPicks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const createGroup = async () => {
    if (!groupName.trim()) { toast.error("Name your group"); return; }
    if (groupPicks.length === 0) { toast.error("Add at least one friend"); return; }
    try {
      const r = await api.post("/groups", { name: groupName.trim(), member_ids: groupPicks });
      setGroupOpen(false); setGroupName(""); setGroupPicks([]);
      navigate(`/groups/${r.data.id}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const handleScan = async (text) => {
    setScanOpen(false);
    let code = null, uid = null;
    try { const d = JSON.parse(text); code = d.fc; uid = d.uid; } catch { code = (text || "").trim(); }
    if (!code && !uid && text && text.includes("add=")) { try { code = new URL(text).searchParams.get("add"); } catch { /* noop */ } }
    try {
      if (code) { const r = await api.post("/friends/request-by-code", { code }); toast.success(`Request sent to ${r.data.name}`); }
      else if (uid) { await api.post("/friends/request", { user_id: uid }); toast.success("Request sent"); }
      else { toast.error("Unrecognized QR code"); }
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(ref?.code || ""); toast.success("Code copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const shareCode = async () => {
    const txt = `Add me on Darkchata! My friend code: ${ref?.code}`;
    if (navigator.share) { try { await navigator.share({ title: "Darkchata", text: txt }); } catch { /* noop */ } return; }
    try { await navigator.clipboard.writeText(txt); toast.success("Invite copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const TABS = [["messages", `Chats (${convos.length + groups.length})`], ["friends", `Friends (${data.friends.length})`], ["requests", `Requests (${data.incoming.length})`]];

  return (
    <Screen>
      <PageHeader overline="Community" title="Darkchata" />

      <div className="flex gap-1.5 mb-5 bg-white/5 border border-white/10 rounded-full p-1">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`friends-tab-${k}`}
            className={`flex-1 py-2.5 rounded-full text-[13px] font-medium transition-colors ${tab === k ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>{label}</button>
        ))}
      </div>

      {tab === "messages" && (
        (convos.length === 0 && groups.length === 0) ? <Empty icon={MessagesSquare} text="No conversations yet. Tap + to add friends or start a group." /> : (
          <div className="space-y-2" data-testid="convo-list">
            {groups.map((g) => (
              <button key={`g-${g.id}`} data-testid={`group-row-${g.id}`} onClick={() => navigate(`/groups/${g.id}`)}
                className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 text-left active:scale-[0.99] transition-transform hover:bg-white/[0.07]">
                <span className="w-12 h-12 rounded-full bg-[#9D4EDD]/20 text-[#9D4EDD] flex items-center justify-center shrink-0 overflow-hidden">{g.avatar_path ? <img src={fileUrl(g.avatar_path)} alt="" className="w-full h-full object-cover" /> : <Users size={22} />}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium truncate">{g.name} <span className="text-[#A19DB0] text-xs font-normal">· {g.members_count}</span></p>
                    {g.last_message && <span className="text-[#A19DB0]/60 text-[11px] shrink-0">{fmtDateTime(g.last_message.created_at)}</span>}
                  </div>
                  <p className={`text-sm truncate mt-0.5 ${g.unread > 0 ? "text-[#E8E4F0] font-medium" : "text-[#A19DB0]"}`}>{g.last_message ? g.last_message.text : "New group — say hi 👋"}</p>
                </div>
                {g.unread > 0 && <span data-testid={`group-unread-${g.id}`} className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-[#D4AF37] text-black text-xs font-bold flex items-center justify-center">{g.unread}</span>}
              </button>
            ))}
            {convos.map((c) => (
              <button key={c.friend.id} data-testid={`convo-${c.friend.id}`} onClick={() => navigate(`/messages/${c.friend.id}`)}
                className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 text-left active:scale-[0.99] transition-transform hover:bg-white/[0.07]">
                <Avatar path={c.friend.avatar_path} picture={c.friend.picture} name={c.friend.name} size="w-12 h-12" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium truncate flex items-center gap-1.5">{c.friend.name} <RoleBadge role={c.friend.role} size="xs" /></p>
                    {c.last_message && <span className="text-[#A19DB0]/60 text-[11px] shrink-0">{fmtDateTime(c.last_message.created_at)}</span>}
                  </div>
                  <p className={`text-sm truncate mt-0.5 ${c.unread > 0 ? "text-[#E8E4F0] font-medium" : "text-[#A19DB0]"}`}>{c.last_message ? c.last_message.text : "Say hello 👋"}</p>
                </div>
                {c.unread > 0 && <span data-testid={`unread-${c.friend.id}`} className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-[#D4AF37] text-black text-xs font-bold flex items-center justify-center">{c.unread}</span>}
              </button>
            ))}
          </div>
        )
      )}

      {tab === "friends" && (
        <>
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 mb-4">
            <Search size={18} className="text-[#A19DB0]" />
            <input value={q} onChange={(e) => search(e.target.value)} data-testid="friends-search" placeholder="Search dancers by name"
              className="bg-transparent outline-none flex-1 text-sm placeholder:text-white/30" />
          </div>
          {results.length > 0 && (
            <div className="space-y-2 mb-6">
              {results.map((u) => (
                <div key={u.id} data-testid={`search-result-${u.id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                  <Avatar path={u.avatar_path} name={u.name} />
                  <p className="flex-1 font-medium">{u.name}</p>
                  <button onClick={() => request(u.id)} data-testid={`add-${u.id}`} className="flex items-center gap-1 bg-[#D4AF37] text-black rounded-full px-3 py-1.5 text-sm font-semibold active:scale-95 transition-transform">
                    <UserPlus size={15} /> Add
                  </button>
                </div>
              ))}
            </div>
          )}
          {data.friends.length === 0 ? <Empty text="No friends yet. Tap + to add someone." /> : (
            <div className="space-y-2">
              {data.friends.map((f) => (
                <button key={f.id} data-testid={`friend-${f.id}`} onClick={() => navigate(`/messages/${f.id}`)} className="w-full text-left flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 active:scale-[0.99] transition-transform hover:bg-white/[0.07]">
                  <Avatar path={f.avatar_path} name={f.name} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate flex items-center gap-1.5">{f.name} <RoleBadge role={f.role} size="xs" /></p>
                    {f.phone && <span data-testid={`friend-phone-${f.id}`} onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${f.phone}`; }} className="text-[#A19DB0] text-xs flex items-center gap-1 mt-0.5 w-fit"><Phone size={11} /> {f.phone}</span>}
                  </div>
                  <span data-testid={`message-${f.id}`} className="w-9 h-9 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0"><MessageCircle size={18} /></span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "requests" && (
        (data.incoming.length === 0 && data.outgoing.length === 0) ? <Empty text="No pending requests." /> : (
          <div className="space-y-2">
            {data.incoming.map((r) => (
              <div key={r.request_id} data-testid={`request-${r.request_id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                <Avatar path={r.avatar_path} name={r.name} />
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{r.name}</p><p className="text-[#A19DB0] text-xs">Wants to connect</p></div>
                <button onClick={() => accept(r.request_id)} data-testid={`accept-${r.request_id}`} className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center active:scale-90 transition-transform"><Check size={18} /></button>
                <button onClick={() => remove(r.id)} data-testid={`decline-${r.request_id}`} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform"><X size={18} /></button>
              </div>
            ))}
            {data.outgoing.map((r) => (
              <div key={r.request_id} data-testid={`outgoing-${r.request_id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 opacity-80">
                <Avatar path={r.avatar_path} name={r.name} />
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{r.name}</p><p className="text-[#A19DB0] text-xs">Request sent</p></div>
                <button onClick={() => remove(r.id)} data-testid={`cancel-req-${r.request_id}`} className="text-[#A19DB0] text-sm px-3 py-1.5 rounded-full bg-white/5 active:scale-90 transition-transform">Cancel</button>
              </div>
            ))}
          </div>
        )
      )}

      {/* Floating action button */}
      <div className="fixed bottom-32 left-1/2 -translate-x-1/2 w-full max-w-md sm:max-w-xl lg:max-w-3xl pointer-events-none z-[55]">
        <button onClick={() => setSheet(true)} data-testid="community-fab"
          className="pointer-events-auto absolute right-4 w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-lg shadow-black/40 active:scale-90 transition-transform gold-glow">
          <Plus size={26} strokeWidth={2.4} />
        </button>
      </div>

      {/* Action sheet */}
      {sheet && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end" onClick={() => setSheet(false)} data-testid="community-sheet">
          <div className="w-full max-w-md mx-auto bg-[#1a1424] border-t border-white/10 rounded-t-3xl p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] mb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-5" />
            <button onClick={() => { setSheet(false); setGroupOpen(true); }} data-testid="sheet-new-group" className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4 mb-2.5 text-left active:scale-[0.98] transition-transform">
              <span className="w-10 h-10 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0"><Users size={20} /></span>
              <div><p className="font-medium">New group</p><p className="text-[#A19DB0] text-xs">Start a group chat with friends</p></div>
            </button>

            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="sheet-add-friend">
              <div className="flex items-center gap-3 px-4 pt-4 pb-2">
                <span className="w-10 h-10 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0"><UserPlus size={20} /></span>
                <div><p className="font-medium">Add a friend</p><p className="text-[#A19DB0] text-xs">Choose how you'd like to connect</p></div>
              </div>
              <div className="divide-y divide-white/5">
                {[
                  { label: "My QR code", desc: "Let a friend scan to add you", icon: QrCode, tid: "sheet-my-qr", fn: () => { setSheet(false); setQrOpen(true); } },
                  { label: "Scan a QR code", desc: "Add a friend by scanning theirs", icon: ScanLine, tid: "sheet-scan", fn: () => { setSheet(false); setScanOpen(true); } },
                  { label: "Add by code", desc: "Enter a friend's 6-digit code", icon: UserPlus, tid: "sheet-add-code", fn: () => { setSheet(false); setAddOpen(true); } },
                ].map((o) => {
                  const Icon = o.icon;
                  return (
                    <button key={o.tid} onClick={o.fn} data-testid={o.tid} className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-white/5 transition-colors">
                      <span className="w-9 h-9 rounded-full bg-[#9D4EDD]/15 text-[#c79bff] flex items-center justify-center shrink-0"><Icon size={18} /></span>
                      <div className="flex-1"><p className="text-sm font-medium">{o.label}</p><p className="text-[#A19DB0] text-xs">{o.desc}</p></div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My QR identity card */}
      {qrOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setQrOpen(false)} data-testid="my-qr-modal">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col items-center">
              <Avatar path={user?.avatar_path} picture={user?.picture} name={user?.name || "You"} size="w-16 h-16" />
              <p className="font-display text-lg font-semibold mt-3">{user?.name}</p>
              <div className="bg-white p-4 rounded-2xl mt-4" data-testid="my-qr-code">
                <QRCode value={JSON.stringify({ fc: ref?.code || "", name: user?.name })} size={180} fgColor="#0D0B14" bgColor="#FFFFFF" />
              </div>
              <p className="text-[#A19DB0] text-xs mt-4">Friend code</p>
              <p className="font-display text-2xl tracking-[0.3em] text-[#D4AF37] mt-1" data-testid="my-friend-code">{ref?.code}</p>
              <p className="text-[#A19DB0] text-xs mt-3 text-center">Have a friend scan this, or share your code to connect.</p>
              <div className="flex gap-2 mt-5 w-full">
                <button onClick={copyCode} data-testid="qr-copy-code" className="flex-1 rounded-full bg-white/10 border border-white/10 py-2.5 text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform"><Copy size={15} /> Copy</button>
                <button onClick={shareCode} data-testid="qr-share-code" className="flex-1 rounded-full bg-[#D4AF37] text-black font-semibold py-2.5 text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform"><Share2 size={15} /> Share</button>
              </div>
              <button onClick={() => setQrOpen(false)} data-testid="my-qr-close" className="mt-3 text-[#A19DB0] text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add by code */}
      {addOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setAddOpen(false)} data-testid="add-code-modal">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-1">Add by friend code</h3>
            <p className="text-[#A19DB0] text-sm mb-4">Enter your friend's 6-character code.</p>
            <form onSubmit={(e) => { e.preventDefault(); submitCode(); }}>
              <input value={addCode} onChange={(e) => setAddCode(e.target.value.toUpperCase())} data-testid="add-code-input" placeholder="e.g. 4F9K2A" maxLength={8} autoFocus
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center font-display text-xl tracking-[0.3em] text-[#D4AF37] outline-none placeholder:text-white/20" />
              <div className="flex gap-2 mt-5">
                <button type="button" onClick={() => { setAddOpen(false); setAddCode(""); }} className="flex-1 rounded-full bg-white/10 border border-white/10 py-2.5 text-sm active:scale-95 transition-transform">Cancel</button>
                <button type="submit" data-testid="add-code-submit" className="flex-1 rounded-full bg-[#D4AF37] text-black font-semibold py-2.5 text-sm active:scale-95 transition-transform">Send request</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {scanOpen && <QRScanModal onClose={() => setScanOpen(false)} onDecode={handleScan} />}

      {/* New group */}
      {groupOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setGroupOpen(false)} data-testid="new-group-modal">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-3">New group</h3>
            <input value={groupName} onChange={(e) => setGroupName(e.target.value)} data-testid="group-name-input" placeholder="Group name" maxLength={60}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none placeholder:text-white/30 mb-4" />
            <p className="text-[#A19DB0] text-xs mb-2">Add friends</p>
            {data.friends.length === 0 ? (
              <p className="text-[#A19DB0] text-sm">Add friends first to create a group.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {data.friends.map((f) => (
                  <button key={f.id} onClick={() => togglePick(f.id)} data-testid={`group-pick-${f.id}`}
                    className={`w-full flex items-center gap-3 rounded-2xl border p-2.5 text-left transition-colors ${groupPicks.includes(f.id) ? "bg-[#D4AF37]/15 border-[#D4AF37]/40" : "bg-white/5 border-white/10"}`}>
                    <Avatar path={f.avatar_path} name={f.name} size="w-9 h-9" />
                    <p className="flex-1 font-medium truncate">{f.name}</p>
                    {groupPicks.includes(f.id) && <Check size={18} className="text-[#D4AF37]" />}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setGroupOpen(false); setGroupName(""); setGroupPicks([]); }} className="flex-1 rounded-full bg-white/10 border border-white/10 py-2.5 text-sm active:scale-95 transition-transform">Cancel</button>
              <button onClick={createGroup} data-testid="group-create-submit" className="flex-1 rounded-full bg-[#D4AF37] text-black font-semibold py-2.5 text-sm active:scale-95 transition-transform disabled:opacity-40" disabled={!groupName.trim() || groupPicks.length === 0}>Create</button>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
}
